import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import type { ScopingAIMessage, ScopingAIMessageInsert, ScopingAIMessageRole } from '@/shared/types/database'

interface AddMessageParams {
  projectId: string
  role: ScopingAIMessageRole
  content: string
  specsUpdate?: Record<string, unknown>
}

/**
 * Fetch all AI scoping messages for a project
 */
async function fetchScopingAIMessages(projectId: string): Promise<ScopingAIMessage[]> {
  const { data, error } = await supabase
    .from('scoping_ai_messages')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true })

  if (error) {
    throw new Error(error.message)
  }

  return data as ScopingAIMessage[]
}

/**
 * Add a new AI scoping message
 */
async function addScopingAIMessage(params: AddMessageParams): Promise<ScopingAIMessage> {
  const insertData: ScopingAIMessageInsert = {
    project_id: params.projectId,
    role: params.role,
    content: params.content,
    specs_update: params.specsUpdate,
  }

  const { data, error } = await supabase
    .from('scoping_ai_messages')
    .insert(insertData)
    .select()
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return data as ScopingAIMessage
}

/**
 * Hook to fetch and manage AI scoping messages for a project
 *
 * @param projectId - The project ID to fetch messages for
 * @returns Query result with messages array
 */
export function useScopingAIMessages(projectId: string | undefined) {
  return useQuery({
    queryKey: ['scopingAIMessages', projectId],
    queryFn: () => fetchScopingAIMessages(projectId!),
    enabled: !!projectId,
  })
}

/**
 * Hook to add a new AI scoping message
 *
 * Automatically invalidates the messages query on success
 */
export function useAddScopingAIMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: addScopingAIMessage,
    onSuccess: (_, variables) => {
      // Invalidate messages for this project
      queryClient.invalidateQueries({
        queryKey: ['scopingAIMessages', variables.projectId]
      })
    },
  })
}

/**
 * Get the last message from the conversation
 */
export function getLastMessage(messages: ScopingAIMessage[]): ScopingAIMessage | undefined {
  return messages.length > 0 ? messages[messages.length - 1] : undefined
}

/**
 * Check if the conversation is empty (no user messages yet)
 */
export function isConversationEmpty(messages: ScopingAIMessage[]): boolean {
  return !messages.some(m => m.role === 'user')
}

/**
 * Convert messages to the format expected by AI APIs
 */
export function messagesToAIFormat(messages: ScopingAIMessage[]): Array<{
  role: ScopingAIMessageRole
  content: string
}> {
  return messages.map(m => ({
    role: m.role,
    content: m.content,
  }))
}
